import * as React from 'react'
import { BrowserRouter, Navigate, useRoutes } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import { createBrowserHistory } from 'history'
import groupBy from 'lodash.groupby'
import compose from 'lodash.flowright'
import { withLDProvider } from 'launchdarkly-react-client-sdk'
import * as Sentry from '@sentry/react'

import type { WithChildren } from '@clara/react-utils'
import { Auth, AuthProvider, AuthVersion, PrivateRoute } from '@clara/auth'
import { Layout } from '@clara/layout-next'
import { LocaleProvider, Locale, COUNTRY } from '@clara/locale'
import { UIProvider, GlobalSpinner } from '@clara/ui'
import { Zendesk } from '@clara/zendesk'
import { __DEV__ } from '@clara/utils'

import { FederatedRouteObject, RouteLayout } from './types/react-router-dom'
import env from './env'
import AllRoutes from './routes'
import { ErrorFallbackGlobal } from './components/ErrorFallbackGlobal'

const locale = new Locale({
  country: process.env.REACT_APP_OPERATION_COUNTRY! as COUNTRY,
  useLocize: true, // prevent hit the Locize service
  locizeVersion: 'latest',
  locizeProjectId: process.env.REACT_APP_LOCIZE_PROJECT_ID,
})

const version = (process.env.REACT_APP_AUTH_VERSION! || 'v2') as AuthVersion

const auth = new Auth({
  url: process.env.REACT_APP_API_URL!,
  version,
  unstable_Redirect: true,
  sift: {
    beaconKey: process.env.REACT_APP_SIFT_BEACON_KEY!,
    isDisabled: __DEV__,
  },
})

// Routes gruped by ´layout´
const routesByLayout = groupBy(
  AllRoutes.map((route) => ({
    ...route,
    element: (
      <React.Suspense fallback={<GlobalSpinner />}>
        {route.element}
      </React.Suspense>
    ),
  })),
  (route) => {
    const layout = route.layout ?? 'fullscreen'
    return layout
  },
) as unknown as Record<RouteLayout, FederatedRouteObject[]>

console.log(routesByLayout)

const routes: FederatedRouteObject[] = [
  {
    path: '/',
    element: (
      <Layout
        appcuesId={env.APPCUES_ID}
        beamerOptions={{
          product_id: env.BEAMER_PRODUCT_ID,
        }}
        referralProgram={{
          widgetTypes: env.REFERRAL_WIDGET_TYPES,
          tenantAlias: env.REFERRAL_TENANT_ALIAS,
          jwtUrl: env.REFERRAL_API_URL,
        }}
        qualtricsProjectId={env.QUALTRICS_PROJECT_ID}
        zendeskHost={env.ZENDESK_HOST}
      />
    ),
    children: routesByLayout.dashboard,
    // children: [
    //   ...routesByLayout.dashboard,
    //   { index: true, element: <PrivateRoute /> },
    //   {
    //     path: '/dashboard',
    //     element: (
    //       <PrivateRoute>
    //         <pre>/dashboard</pre>
    //         <Link to="/help-center">help center</Link>
    //         <Link to="/approvals-flow/reimbursement">Approvals Flow</Link>
    //         <Link to="/tasks">Inbox</Link>
    //       </PrivateRoute>
    //     ),
    //   },
    // ],
  },
  ...(routesByLayout.fullscreen ?? []),
]

const onRedirectCallback = (appState: any) => {
  createBrowserHistory().push(
    appState && appState.returnTo
      ? appState.returnTo
      : window.location.pathname,
  )
}

const AppProvider = ({ children }: WithChildren) => {
  return (
    <UIProvider>
      <BrowserRouter>
        <LocaleProvider locale={locale}>
          {({ lang }) => (
            <AuthProvider
              {...{
                auth,
                domain: String(process.env.REACT_APP_AUTH0_DOMAIN),
                clientId: String(process.env.REACT_APP_AUTH0_CLIENT_ID),
                onRedirectCallback,
                cacheLocation: 'localstorage',
                authorizationParams: {
                  redirect_uri: `${window.location.origin}${process.env.REACT_APP_AUTH0_REDIRECT_PATH}`,
                  audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                },
              }}
            >
              <Zendesk
                locale={lang.toLowerCase()}
                snippetId={process.env.REACT_APP_ZENDESK_SNIPPET_KEY!}
                zEChatId={process.env.REACT_APP_ZENDESK_PLAN_CHAT_ID}
                zendeskArticlesIds={{
                  banner: {
                    internalBlock:
                      process.env
                        .REACT_APP_ZENDESK_BANNER_INTERNAL_BLOCK_ARTICLE_ID,
                  },
                  importantLinks: {
                    reportTransaction:
                      process.env
                        .REACT_APP_ZENDESK_IMPORTANT_LINKS_REPORT_TRANSACTION_ARTICLE_ID,
                    transactionDeclined:
                      process.env
                        .REACT_APP_ZENDESK_IMPORTANT_LINKS_TRANSACTION_DECLINED_ARTICLE_ID,
                  },
                  sendMessage: {
                    email:
                      process.env
                        .REACT_APP_ZENDESK_SEND_MESSAGE_EMAIL_ARTICLE_ID,
                    feedback:
                      process.env
                        .REACT_APP_ZENDESK_SEND_MESSAGE_FEEDBACK_ARTICLE_ID,
                  },
                }}
              >
                <HelmetProvider>{children}</HelmetProvider>
              </Zendesk>
            </AuthProvider>
          )}
        </LocaleProvider>
      </BrowserRouter>
    </UIProvider>
  )
}

const App = () => {
  const element = useRoutes(routes)

  return <React.Fragment>{element}</React.Fragment>
}
const ConnectedApp = () => {
  const app = (
    <AppProvider>
      <App />
    </AppProvider>
  )
  return env.SENTRY_ENABLED === 'true' ? (
    <Sentry.ErrorBoundary fallback={<ErrorFallbackGlobal />}>
      {app}
    </Sentry.ErrorBoundary>
  ) : (
    app
  )
}

const withFeatureFlags = withLDProvider({
  clientSideID: process.env.REACT_APP_FEATURE_FLAG_CLIENT_SIDE_ID!,
  user: {
    key: process.env.REACT_APP_FEATURE_FLAG_CLIENT_USER_KEY,
    name: process.env.REACT_APP_FEATURE_FLAG_CLIENT_USER_NAME,
    email: process.env.REACT_APP_FEATURE_FLAG_CLIENT_USER_EMAIL,
  },
})

// prettier-ignore
export default compose(
	withFeatureFlags,
	React.memo)
	(ConnectedApp)
